"use client"

import { Copy, Check, RotateCcw, Pencil, ThumbsUp, ThumbsDown, Send, X, Loader2 } from "lucide-react"
import { useState, useRef } from "react"
import { Button } from "@/components/ui/button"
import { DisabledWithReason } from "@/components/ui/disabled-with-reason"
import { useAiCredits } from "@/hooks/ai/use-ai-credits"
import { AI_DISABLED_REASON } from "@/lib/ai/quota-copy"
import { cn } from "@/lib/utils"
import { fetchApi } from "@/lib/api/client"

interface MessageActionsProps {
  content: string
  role: "user" | "assistant"
  isLast: boolean
  messageId?: string
  conversationId?: string | null
  onRegenerate?: () => void
  onEdit?: () => void
}

type FeedbackState = "none" | "up" | "down"

export function MessageActions({
  content,
  role,
  isLast,
  messageId,
  conversationId,
  onRegenerate,
  onEdit,
}: MessageActionsProps) {
  const [copied, setCopied] = useState(false)
  const [feedback, setFeedback] = useState<FeedbackState>("none")
  const [showFeedbackForm, setShowFeedbackForm] = useState(false)
  const [feedbackText, setFeedbackText] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Read-only: this row renders once per message, and the panel that owns the
  // transcript already owns the fetch. A per-row poll would multiply one
  // request by the length of the conversation.
  //
  // Regenerate is the only action here that SPENDS — copy, edit, and the
  // thumbs are free and stay available whatever the allowance says.
  const { isBlocked } = useAiCredits({ enabled: false })

  const handleCopy = () => {
    navigator.clipboard.writeText(content)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleThumbsUp = () => {
    setFeedback("up")
    setShowFeedbackForm(false)
  }

  const handleThumbsDown = () => {
    setFeedback("down")
    setShowFeedbackForm(true)
    setTimeout(() => textareaRef.current?.focus(), 100)
  }

  const handleSubmitFeedback = async () => {
    if (!feedbackText.trim()) return
    setSubmitting(true)
    try {
      await fetchApi("/feedback", {
        method: "POST",
        body: JSON.stringify({
          feedback: feedbackText,
          category: "bug_report",
          priority: "medium",
          affectedArea: "AI Chat",
          aiGenerated: false,
          pathname: "/recipients/ai-chat",
          url: typeof window !== "undefined" ? window.location.href : "",
          chatHistory: conversationId
            ? [
                { role: "assistant", content, type: "text", timestamp: new Date().toISOString() },
                { role: "user", content: `[Thumbs down feedback] ${feedbackText}`, type: "text", timestamp: new Date().toISOString() },
              ]
            : undefined,
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
          screenResolution: typeof window !== "undefined" ? `${window.innerWidth}x${window.innerHeight}` : "",
        }),
      })
      setSubmitted(true)
      setShowFeedbackForm(false)
    } catch {
      // silent
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
          onClick={handleCopy}
          title="Copy message"
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        </Button>

        {role === "user" && onEdit && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
            onClick={onEdit}
            title="Edit & resend"
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        )}

        {role === "assistant" && isLast && onRegenerate && (
          <DisabledWithReason
            disabled={isBlocked}
            reason={AI_DISABLED_REASON.exhausted}
          >
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-foreground"
              onClick={onRegenerate}
              disabled={isBlocked}
              title="Regenerate response"
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </Button>
          </DisabledWithReason>
        )}

        {/* Thumbs up/down for assistant messages */}
        {role === "assistant" && !submitted && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "h-7 w-7 transition-colors",
                feedback === "up"
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-muted-foreground hover:text-foreground"
              )}
              onClick={handleThumbsUp}
              title="Good response"
            >
              <ThumbsUp className={cn("h-3.5 w-3.5", feedback === "up" && "fill-current")} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "h-7 w-7 transition-colors",
                feedback === "down"
                  ? "text-red-500 dark:text-red-400"
                  : "text-muted-foreground hover:text-foreground"
              )}
              onClick={handleThumbsDown}
              title="Bad response"
            >
              <ThumbsDown className={cn("h-3.5 w-3.5", feedback === "down" && "fill-current")} />
            </Button>
          </>
        )}

        {submitted && (
          <span className="text-[11px] text-muted-foreground flex items-center gap-1 px-1">
            <Check className="h-3 w-3 text-emerald-500" />
            Feedback submitted
          </span>
        )}
      </div>

      {/* Feedback form — appears inline below the message actions on thumbs down */}
      {showFeedbackForm && (
        <div className="flex flex-col gap-1.5 rounded-lg border border-border bg-muted/30 p-2 max-w-sm animate-in fade-in slide-in-from-top-1 duration-200">
          <p className="text-[11px] text-muted-foreground font-medium">What went wrong?</p>
          <textarea
            ref={textareaRef}
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            placeholder="The response was inaccurate, incomplete, or unhelpful because..."
            rows={2}
            className="w-full resize-none rounded-md border border-border bg-background px-2.5 py-1.5 text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault()
                handleSubmitFeedback()
              }
            }}
          />
          <div className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-[11px] px-2"
              onClick={() => {
                setShowFeedbackForm(false)
                setFeedback("none")
              }}
            >
              <X className="h-3 w-3 me-1" />
              Cancel
            </Button>
            <Button
              size="sm"
              className="h-6 text-[11px] px-2"
              onClick={handleSubmitFeedback}
              disabled={!feedbackText.trim() || submitting}
            >
              {submitting ? (
                <Loader2 className="h-3 w-3 animate-spin me-1" />
              ) : (
                <Send className="h-3 w-3 me-1" />
              )}
              Submit
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
